// Busca sob demanda dos dados que o Kanban NÃO carrega.
//
// O board roda com um payload deliberadamente enxuto (ver NEG_COLUNAS/NEG_SELECT
// em KanbanBoard): sem documentos_json, sem notas, com a UC reduzida a
// numero_uc + cidade. Engordar aquele select para servir ao export penalizaria
// toda pintura do board. Então o export busca o resto aqui, uma vez, pelos ids
// já filtrados na tela.
//
// Dois cuidados obrigatórios: `.in()` com milhares de ids estoura a URL (daí o
// chunk) e o PostgREST corta a resposta em 1.000 linhas (daí o range por
// página) — o mesmo teto que já mordeu o Analytics e o próprio Kanban.
import { supabase } from '../../../../src/lib/supabase';

export type Dataset = 'extras' | 'ucs' | 'notas' | 'analises' | 'avalistas' | 'tarefas' | 'logs';

export interface DadosExport {
    /** Colunas de `negociacoes` que ficam fora do select do board, por id. */
    extras: Map<number, any>;
    ucs: Map<number, any[]>;
    notas: Map<number, any[]>;
    analises: Map<number, any[]>;
    avalistas: Map<number, any[]>;
    tarefas: Map<number, any[]>;
    logs: Map<number, any[]>;
}

// 400 ids por `in()` deixa a query string em ~3 KB com ids de 4 dígitos — folgado
// para o limite de URL do PostgREST e metade das idas de um lote de 200.
const CHUNK_IDS = 400;
const PAGE = 1000;

const COLUNAS: Record<Dataset, { tabela: string; select: string; ordem: string }> = {
    // documentos_json é a coluna mais pesada da tabela — vem só aqui, no clique
    // do export, nunca no carregamento do board.
    extras: {
        tabela: 'negociacoes',
        select:
            'id, documentos_json, id_termo_autentique, boas_vindas_enviadas, pode_reanalisar, ' +
            'numero_medidor, medidor_foto_url, medidor_capturado_em, medidor_capturado_por',
        ordem: 'id',
    },
    ucs: { tabela: 'unidades_consumidoras', select: '*', ordem: 'numero_uc' },
    notas: {
        tabela: 'negociacao_notas',
        select: 'id, negociacao_id, user_id, conteudo, tipo, created_at, arquivo_nome, arquivo_url, transcricao',
        ordem: 'created_at',
    },
    analises: { tabela: 'analise_cadastral', select: '*', ordem: 'id' },
    avalistas: { tabela: 'negociacao_avalistas', select: '*', ordem: 'id' },
    tarefas: {
        tabela: 'tarefas',
        select:
            'id, negociacao_id, titulo, descricao, tipo, categoria, modalidade, objetivo, status, prioridade, ' +
            'responsavel_id, solicitante_id, vencimento, hora_visita, data_inicio, data_conclusao, ' +
            'resultado_categoria, origem_atribuicao, motivo_sugestao, created_at',
        ordem: 'vencimento',
    },
    logs: {
        tabela: 'negociacao_logs',
        select: 'id, negociacao_id, user_id, acao, detalhes, created_at',
        ordem: 'created_at',
    },
};

const emLotes = <T>(itens: T[], tamanho: number): T[][] => {
    const lotes: T[][] = [];
    for (let i = 0; i < itens.length; i += tamanho) lotes.push(itens.slice(i, i + tamanho));
    return lotes;
};

/** Uma tabela inteira para o recorte de ids, paginando cada lote até esgotar. */
async function carregarDataset(dataset: Dataset, ids: number[], onLote: () => void): Promise<any[]> {
    const { tabela, select, ordem } = COLUNAS[dataset];
    const coluna = dataset === 'extras' ? 'id' : 'negociacao_id';
    const linhas: any[] = [];

    for (const lote of emLotes(ids, CHUNK_IDS)) {
        for (let pagina = 0; ; pagina++) {
            const de = pagina * PAGE;
            // `id` como desempate: ordenação só por data/número repete ou pula
            // linha entre páginas quando há empate, e ninguém percebe numa planilha.
            let q = supabase.from(tabela).select(select).in(coluna, lote).order(ordem, { ascending: true });
            if (ordem !== 'id') q = q.order('id', { ascending: true });
            const { data, error } = await q.range(de, de + PAGE - 1);
            if (error) throw new Error(`Falha ao carregar ${tabela}: ${error.message}`);
            const bloco = data || [];
            linhas.push(...bloco);
            if (bloco.length < PAGE) break;
        }
        onLote();
    }
    return linhas;
}

const agrupar = (linhas: any[], chave: string): Map<number, any[]> => {
    const mapa = new Map<number, any[]>();
    for (const linha of linhas) {
        const id = Number(linha[chave]);
        const atual = mapa.get(id);
        if (atual) atual.push(linha);
        else mapa.set(id, [linha]);
    }
    return mapa;
};

export async function carregarDadosExport(
    ids: number[],
    datasets: Set<Dataset>,
    onProgresso: (feito: number, total: number, etapa: string) => void
): Promise<DadosExport> {
    const vazio = (): DadosExport => ({
        extras: new Map(),
        ucs: new Map(),
        notas: new Map(),
        analises: new Map(),
        avalistas: new Map(),
        tarefas: new Map(),
        logs: new Map(),
    });
    const resultado = vazio();
    if (ids.length === 0) return resultado;

    const lotesPorDataset = Math.ceil(ids.length / CHUNK_IDS);
    const total = datasets.size * lotesPorDataset;
    let feito = 0;

    const rotulos: Record<Dataset, string> = {
        extras: 'documentos e medidor',
        ucs: 'unidades consumidoras',
        notas: 'notas',
        analises: 'validação de dados',
        avalistas: 'avalistas',
        tarefas: 'tarefas e visitas',
        logs: 'histórico de ações',
    };
    let etapa = rotulos[Array.from(datasets)[0]];

    // Os sete conjuntos são independentes entre si — em série, a carteira inteira
    // dá ~140 idas ao servidor enfileiradas (uns 20 s). Cada dataset pagina em
    // série (a página seguinte depende da anterior), mas os datasets correm juntos,
    // então o tempo de parede vira o do maior deles.
    await Promise.all(
        Array.from(datasets).map(async dataset => {
            const linhas = await carregarDataset(dataset, ids, () => {
                feito += 1;
                etapa = rotulos[dataset];
                onProgresso(feito, total, etapa);
            });
            if (dataset === 'extras') {
                for (const linha of linhas) resultado.extras.set(Number(linha.id), linha);
            } else {
                resultado[dataset] = agrupar(linhas, 'negociacao_id');
            }
        })
    );

    onProgresso(total, total, 'montando a planilha');
    return resultado;
}
